import type {ChangeEvent} from 'react';
import {useState} from 'react';
import {toast} from 'react-toastify';

import {merchantService} from '@/services/merchants';
import type {LedgerReasons} from '@/types';
import {AfriexWalletType} from '@/types';
import type {AfriexBusiness} from '@/types/AfriexBusiness';
import type {ICurrency} from '@/types/Currency';
import {getResponseMessage, getThrownMessage} from '@/utils/errorMessage';

type Response = {
  message: string;
  status: string;
};

// Payout has no walletTag on the backend (an empty/omitted walletTag is
// treated as Payout); AfriexWalletType.Withdraw is the shared UI id for it
// (matches the wallet-balance tabs), stripped down to '' only at submit time.
export const WALLET_TYPE_LABELS: Record<AfriexWalletType, string> = {
  [AfriexWalletType.Withdraw]: 'Payout',
  [AfriexWalletType.Deposit]: 'Collection',
  [AfriexWalletType.Fee]: 'Fee',
};

const useFundBusinessWallet = (
  business: AfriexBusiness,
  walletType: AfriexWalletType,
  initialAction: 'credit' | 'debit',
  initialResponse: Response,
) => {
  const [amount, setAmount] = useState<number>(0);
  const [currency, setCurrency] = useState<ICurrency | null>(null);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [response, setResponse] = useState(initialResponse);
  const [action, setAction] = useState<'credit' | 'debit'>(initialAction);
  const [fundWalletReason, setFundWalletReason] = useState<LedgerReasons>();
  const [note, setNote] = useState('');

  const handleNoteChange = (event: ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value);
  };

  // The rendered control is a `<Select>` (a `<select>` under the hood); a
  // `HTMLFormElement` target has no real `value`, and this project's
  // TypeScript/React setup resolves that generic parameter's members to
  // `any` rather than erroring, which is what let `event.target.value` reach
  // `setFundWalletReason` untyped.
  const handleSelectReason = (event: ChangeEvent<HTMLSelectElement>) => {
    setFundWalletReason(event.target.value as LedgerReasons);
  };

  const handleAmountChange = (_name: string, value: number) => {
    setAmount(value);
  };

  const handleCurrencyChange = (_name: string, value: ICurrency) => {
    setCurrency(value);
  };

  const handleLedgerButtonClick = (action: 'debit' | 'credit') => {
    setAction(action);
    setShowModal(true);
  };

  const isValidTransaction = () => {
    if (currency && fundWalletReason && (amount > 0 || !isNaN(amount))) {
      return false;
    }
    return true;
  };

  async function handleLedger() {
    try {
      if (!(business.id && currency && fundWalletReason)) {
        return;
      }

      setLoading(true);
      await merchantService.updateWalletBalance(
        action?.toUpperCase() as 'CREDIT' | 'DEBIT',
        {
          currencyCode: currency.code,
          value: amount,
        },
        fundWalletReason,
        business.id,
        walletType === AfriexWalletType.Withdraw ? '' : walletType,
        note,
      );

      setLoading(false);
      setShowModal(false);
      setAmount(0);
      setNote('');
      toast.success(
        `Successfully ${action}ed ${currency?.code} ${amount} ${
          action === 'debit' ? 'from' : 'to'
        } ${business.name}'s Wallet`,
      );
      setResponse({
        status: 'success',
        message: `Successfully ${action}ed ${currency?.code} ${amount} ${
          action === 'debit' ? 'from' : 'to'
        } ${business.name}'s Wallet`,
      });
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (err) {
      setLoading(false);
      setShowModal(false);
      const message =
        getResponseMessage(err) ||
        getThrownMessage(err) ||
        'Unknown Error! Contact Admin';
      setResponse({status: 'error', message});
    }
  }
  return {
    amount,
    currency,
    loading,
    showModal,
    response,
    action,
    handleAmountChange,
    handleCurrencyChange,
    handleLedgerButtonClick,
    isValidTransaction,
    handleLedger,
    setShowModal,
    setResponse,
    handleSelectReason,
    fundWalletReason,
    note,
    handleNoteChange,
  };
};

export default useFundBusinessWallet;
